import React from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import { loadRemoteModule } from "@cw-elements/mfe-setup/load";
import { useConfig } from "@cw-elements/config";
import { useAppState } from "../../../lib/appContext/AppContext";
import { useTrialsRouter } from "../../../shared/custom-hooks/useTrialsRouter";
import { formatTranslation } from "../../utils/translationUtils";

interface PrescriptionExporterProps {
  userLanguage: string;
  isOpen: boolean;
  onClose: () => void;
  orgId: string;
  agOperationIds: () => string[];
  orgCountry: string;
  getConfigInstance: () => unknown;
}

export interface PrescriptionExporterMfeLauncherProps {
  isOpen: boolean;
  onClose: () => void;
  agOperationIds: string[];
  userLanguage?: string;
  loadingFallback?: React.ReactNode;
  errorFallback?: React.ReactElement;
}

const PrescriptionExporter = React.lazy(async () => {
  const module = await loadRemoteModule<PrescriptionExporterProps>(
    "connect-files/prescription-exporter"
  );

  return { default: module.default };
});

/**
 * Renders the prescription exporter remote modal and injects org context and config.
 *
 * @param props Modal state, ag-operation ids, locale, and optional suspense/error fallbacks.
 * @returns The prescription exporter launcher component.
 */
function PrescriptionExporterMfeLauncher({
  isOpen,
  onClose,
  agOperationIds,
  userLanguage = "en",
  loadingFallback,
  errorFallback,
}: Readonly<PrescriptionExporterMfeLauncherProps>): React.JSX.Element {
  const { stateValue } = useAppState();
  const config = useConfig();
  const { orgId } = useTrialsRouter();
  const { t } = useTranslation();
  const orgCountry =
    stateValue.orgsData?.content?.find((org: any) => org.id === orgId)
      ?.country ?? "US";
  const resolvedLoadingFallback =
    loadingFallback ??
    formatTranslation(t("prescriptionExporterModal.loadingMfe"));
  const resolvedErrorFallback = errorFallback ?? (
    <span>{formatTranslation(t("prescriptionExporterModal.mfeError"))}</span>
  );

  return (
    <ErrorBoundary fallback={resolvedErrorFallback}>
      <React.Suspense fallback={resolvedLoadingFallback}>
        <PrescriptionExporter
          userLanguage={userLanguage}
          isOpen={isOpen}
          onClose={onClose}
          orgId={orgId ?? ""}
          agOperationIds={() => agOperationIds}
          orgCountry={orgCountry}
          getConfigInstance={() => config}
        />
      </React.Suspense>
    </ErrorBoundary>
  );
}

export default PrescriptionExporterMfeLauncher;
